/* ServiceNowComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./ServiceNowRenderer");
const SyncWorkSummary = require("../veils/SyncWork/SyncSummary/SyncSummaryComponent");
const _ = require("lodash");

/**
 * class CaptureComponent
 *
 * @fires CaptureComponent#DataUpdated
 */
class ServiceNowComponent extends EventEmitter {
    /**
     *
     * @param {CaptureEntity.} CaptureEntity
     * @param {UserSession} userSession
     * @param {UiUtils} uiUtils
     * @param {CoreLogger} logger
     */
    constructor(
        userSession,
        uiUtils,
        logger,
        userFeedback,
        OrganizationService,
        DataSyncConfig,
        steveIo,
        DeviceEntity,
        Explorables,
        storageFactory,
        ValueMap,
        Router,
        dataSyncEntity,
        serviceConnectionEntities,
        ServiceConnectionEntityDef
    ) {
        super();
        this._userSession = userSession;
        this._logger = logger;
        this._muf = userFeedback;
        this._uiUtils = uiUtils;
        this._organizationService = OrganizationService;
        this._organizationData = {};
        this._dataSyncConfig = DataSyncConfig;
        this._Explorables = Explorables;
        this._storageFactory = storageFactory;
        this._ValueMapEntity = ValueMap;
        this._router = Router;
        this._explorablesEntity = undefined;
        this._dataForSlickTable = undefined;
        this._dataForTree = [];
        this._selectedClass = "cmdb_ci";
        this._steveIo = steveIo;
        this._DeviceEntityDef = DeviceEntity;
        this._fileFactory = steveIo.fileFactory;
        this._snDataSyncConfigEntity = dataSyncEntity;
        this._isDnCLErrored = false;
        this._isDnCSErrored = false;
        this._classAttributes = undefined;
        this._nodePropData = {};
        this._dataForSlickTableSyncing = undefined;
        this._D2CSIdx = 0;
        this._D2CLIdx = 0;
        this._cmdbConnectionFailed = false;
        this._noVendorMap = false;
        this._serviceConnectionEntities = serviceConnectionEntities || [];
        this._ServiceConnectionEntityDef = ServiceConnectionEntityDef;
        this._hasConnections = !!this._serviceConnectionEntities.length;
        this._serviceConnectionEntity = serviceConnectionEntities.find(
            (entity) => entity.id === dataSyncEntity.serviceConnectionId
        );

        this._renderer = new Renderer(this, uiUtils);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    shutdown() {
        this._unregisterListeners();

        const shutdownPromises = [this._renderer.shutdown()];

        return Promise.all(shutdownPromises)
            .then(() => {
                const propertiesToReset = [
                    "_userSession",
                    "_logger",
                    "_muf",
                    "_uiUtils",
                    "_organizationService",
                    "_organizationData",
                    "_dataSyncConfig",
                    "_Explorables",
                    "_storageFactory",
                    "_explorablesEntity",
                    "_dataForSlickTable",
                    "_dataForTree",
                    "_selectedClass",
                    "_steveIo",
                    "_DeviceEntityDef",
                    "_fileFactory",
                    "_isDnCLErrored",
                    "_isDnCSErrored",
                    "_classAttributes",
                    "_renderer",
                    "_nodePropData",
                    "_deviceNodeTreeData",
                    "_dataForCiAttrDropdown",
                    "_dataForSlickTableSyncing",
                    "_selectedClassSyncing",
                    "_classesFiltered",
                    "_dataForTreeFiltered",
                    "errLinkingIds",
                    "_cmdbConnectionFailed",
                    "_noVendorMap",
                    "_serviceConnectionEntities",
                    "_ServiceConnectionEntityDef",
                    "errSyncingIds",
                ];

                for (const property of propertiesToReset) {
                    this[property] = undefined;
                }
            })
            .catch((err) => {
                console.error("Error shutting down ServiceNowComponent: ", err);
            });
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getActiveOrgId() {
        return _.get(this._userSession, "activeOrganization.id");
    }

    get activeOrgName() {
        return this.getActiveOrgName();
    }

    getActiveOrgName() {
        return _.get(this._userSession, "activeOrganization.name");
    }

    async whenModelIsReady() {
        try {
            await this.whenModelIsReadyProm();
            await this._loadClasses();
            await this._loadSelectedClasses();
            await this._loadDevicePropsData();
            await this._loadMappingAttributes();
            await this._loadVendorOsMap();
            this._connectionOptions = this._getConnectionOptions();
        } catch (err) {
            this._logger.warn(err, "ServiceNowComponent.whenModelIsReady() error.");
        }
    }

    whenModelIsReadyProm() {
        return this.loadOrganizationData();
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /*******************************************/

    /*******************************************/
    /* START SLICK DATA METHODS                */
    /*******************************************/

    // Device And Ci Linking Data - Start

    //
    // Linking Helper Functions
    //

    // Return path from root to className
    _getPath(attrDefs, className) {
        if (_.isEmpty(attrDefs)) {
            return [];
        }

        let path = [];

        while (className) {
            path.unshift(className);
            className = attrDefs[className] && attrDefs[className].parent;
        }
        return path;
    }

    // Generate list of derived linking rules
    _generateDerivedLinkingRules(attrDefs, linkingRules, className) {
        const path = this._getPath(attrDefs, className);
        const derivedRules = [];

        // Helper function to find an existing rule by devicePropPath
        function findExistingRule(rules, devicePropPath, cmdbAttr) {
            return rules.find(
                (rule) =>
                    JSON.stringify(rule.devicePropPath) === JSON.stringify(devicePropPath) &&
                    rule.cmdbAttr === cmdbAttr
            );
        }

        // Iterate through the path excluding the last element (the selected class)
        for (let i = 0; i < path.length - 1; i++) {
            const currentClass = path[i];
            const rules = linkingRules[currentClass] || [];

            rules.forEach((rule) => {
                const existingRule = findExistingRule(
                    derivedRules,
                    rule.devicePropPath,
                    rule.cmdbAttr
                );

                if (existingRule) {
                    if (rule.ignorePlaceholder) {
                        // Copy values from the original existing rule in the placeholder
                        rule.cmdbClass = existingRule.cmdbClass;
                        rule.cmdbAttr = existingRule.cmdbAttr;
                        rule.cmdbAttrType = existingRule.cmdbAttrType;
                        rule.devicePropType = existingRule.devicePropType;
                    }
                    // Replace matching existingRule with new rule
                    derivedRules.splice(derivedRules.indexOf(existingRule), 1, rule);
                } else {
                    if (rule.ignorePlaceholder) {
                        // Orphan placeholder with no parent rule as existingRule is empty
                        this.logger.warn(
                            {},
                            "ServiceNowComponent._generateDerivedLinkingRules: placeholder rule is an orphan"
                        );
                        console.log(rule);
                    } else {
                        // Add new rule to derivedRules
                        derivedRules.push(rule);
                    }
                }
            });
        }

        // Process rules of the selected class
        const selectedClassRules = linkingRules[className] || [];
        selectedClassRules.forEach((rule) => {
            const existingRule = findExistingRule(derivedRules, rule.devicePropPath, rule.cmdbAttr);

            if (existingRule) {
                if (rule.ignorePlaceholder) {
                    // Copy values from the original existing rule in the placeholder
                    rule.cmdbClass = existingRule.cmdbClass;
                    rule.cmdbAttr = existingRule.cmdbAttr;
                    rule.cmdbAttrType = existingRule.cmdbAttrType;
                    rule.devicePropType = existingRule.devicePropType;
                    // Remove the existing rule
                    derivedRules.splice(derivedRules.indexOf(existingRule), 1, rule);
                } else {
                    if (!rule.ignore) {
                        // Remove derivedRule because local not ignored rule prevails
                        derivedRules.splice(derivedRules.indexOf(existingRule), 1);
                    }
                }
            }
        });

        return derivedRules;
    }

    // Generate list of derived syncing rules
    _generateDerivedSyncingRules(attrDefs, syncingRules, className) {
        const path = this._getPath(attrDefs, className);
        const derivedRules = [];

        // Helper function to find an existing rule by devicePropPath
        function findExistingRule(rules, devicePropPath, cmdbAttr, syncDirection) {
            return rules.find(
                (rule) =>
                    JSON.stringify(rule.devicePropPath) === JSON.stringify(devicePropPath) &&
                    rule.cmdbAttr === cmdbAttr &&
                    rule.syncDirection === syncDirection
            );
        }

        // Iterate through the path excluding the last element (the selected class)
        for (let i = 0; i < path.length - 1; i++) {
            const currentClass = path[i];
            const rules = syncingRules[currentClass] || [];

            rules.forEach((rule) => {
                const existingRule = findExistingRule(
                    derivedRules,
                    rule.devicePropPath,
                    rule.cmdbAttr,
                    rule.syncDirection
                );

                if (existingRule) {
                    if (rule.ignorePlaceholder) {
                        // Copy values from the original existing rule in the placeholder
                        rule.cmdbClass = existingRule.cmdbClass;
                        rule.cmdbAttr = existingRule.cmdbAttr;
                        rule.cmdbAttrType = existingRule.cmdbAttrType;
                        rule.devicePropType = existingRule.devicePropType;
                        rule.syncDirection = existingRule.syncDirection;
                    }
                    // Replace matching existingRule with new rule
                    derivedRules.splice(derivedRules.indexOf(existingRule), 1, rule);
                } else {
                    if (rule.ignorePlaceholder) {
                        // Orphan placeholder with no parent rule as existingRule is empty
                        this.logger.warn(
                            {},
                            "ServiceNowComponent._generateDerivedLinkingRules: placeholder rule is an orphan"
                        );
                        console.log(rule);
                    } else {
                        // Add new rule to derivedRules
                        derivedRules.push(rule);
                    }
                }
            });
        }

        // Process rules of the selected class
        const selectedClassRules = syncingRules[className] || [];
        selectedClassRules.forEach((rule) => {
            const existingRule = findExistingRule(
                derivedRules,
                rule.devicePropPath,
                rule.cmdbAttr,
                rule.syncDirection
            );

            if (existingRule) {
                if (rule.ignorePlaceholder) {
                    // Copy values from the original existing rule in the placeholder
                    rule.cmdbClass = existingRule.cmdbClass;
                    rule.cmdbAttr = existingRule.cmdbAttr;
                    rule.cmdbAttrType = existingRule.cmdbAttrType;
                    rule.devicePropType = existingRule.devicePropType;
                    rule.syncDirection = existingRule.syncDirection;
                    // Remove the existing rule
                    derivedRules.splice(derivedRules.indexOf(existingRule), 1, rule);
                } else {
                    if (!rule.ignore) {
                        // Remove derivedRule because local not ignored rule prevails
                        derivedRules.splice(derivedRules.indexOf(existingRule), 1);
                    }
                }
            }
        });

        return derivedRules;
    }

    /**
     * Generate Device And Ci Linking Data
     * @param {*} selectedTreeNode
     */
    async _generateSlickDataForInstanceLinking(selectedTreeNode = undefined) {
        try {
            await this._loadSlickDropdownsData(selectedTreeNode);
            this._dataForSlickTable = [];
            this._dataForSlickTableDerived = [];
            this.errLinkingIds = [];

            const dataForLinkingCriteria =
                    this._snDataSyncConfigEntity.getInstanceLinkingCriteria() || {},
                dataForCurrentClass = dataForLinkingCriteria[selectedTreeNode] || [],
                dataForDerivedClassRules = this._generateDerivedLinkingRules(
                    this._classAttributes,
                    dataForLinkingCriteria,
                    selectedTreeNode
                );

            dataForDerivedClassRules.forEach((item, idx) => {
                this._dataForSlickTableDerived.push({
                    solutions: this._nodePropData,
                    deviceNodeTreeData: this._deviceNodeTreeData,
                    ciAttributesDropDownData: this._dataForCiAttrDropdown,
                    ciAttributesData: this._classAttributes,
                    id: `id_${idx}`,
                    ignore: item["ignore"] || false,
                    devicePropPath: item["devicePropPath"],
                    devicePropType: this.findPathForDeviceProps(
                        item["devicePropPath"],
                        this._nodePropData
                    ),
                    cmdbAttr: item["cmdbAttr"],
                    cmdbClass: this._selectedClass,
                    sourceCmdbClass: item["cmdbClass"],
                    cmdbAttrType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                });
            });

            dataForCurrentClass.forEach((item, idx) => {
                if (item.ignorePlaceholder !== true) {
                    this._dataForSlickTable.push({
                        solutions: this._nodePropData,
                        deviceNodeTreeData: this._deviceNodeTreeData,
                        ciAttributesDropDownData: this._dataForCiAttrDropdown,
                        ciAttributesData: this._classAttributes,
                        id: `id_${idx}`,
                        ignore: item["ignore"] || false,
                        devicePropPath: item["devicePropPath"],
                        devicePropType: this.findPathForDeviceProps(
                            item["devicePropPath"],
                            this._nodePropData
                        ),
                        cmdbAttr: item["cmdbAttr"],
                        cmdbClass: this._selectedClass,
                        cmdbAttrType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                    });
                    //we should track the last used idx so we always use a unique higher value
                    this._D2CLIdx = idx;
                }
            });

            this.emit("RefreshDeviceAndCiLinkingGrid");
        } catch (err) {
            return Promise.reject(err);
        }
    }

    /**
     * Add Device And Ci Linking Row
     */
    addNewD2CLinkingRule() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this.findFirstNode(this._nodePropData);

        if (!_.isEmpty(this._dataForCiAttrDropdown)) {
            this._dataForSlickTable.push({
                solutions: this._nodePropData,
                deviceNodeTreeData: this._deviceNodeTreeData,
                ciAttributesDropDownData: this._dataForCiAttrDropdown,
                ciAttributesData: this._classAttributes,
                id: `id_${idx}`,
                ignore: _.get(firstDevicePropNode, "ignore", false),
                devicePropPath: _.get(firstDevicePropNode, "path", []),
                devicePropType: _.get(firstDevicePropNode, "dataType", ""),
                cmdbAttr: this._dataForCiAttrDropdown ? this._dataForCiAttrDropdown[0].value : "",
                cmdbAttrType: this.findType(
                    this._dataForCiAttrDropdown,
                    this._dataForCiAttrDropdown ? this._dataForCiAttrDropdown[0].value : ""
                ),
                cmdbClass: this._selectedClass,
            });

            this.setInstanceLinking();

            this.emit("RefreshDeviceAndCiLinkingGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Instance Linking Criteria for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }
    // Device And Ci Linking Data - End

    // Device And Ci Syncing Data - Start
    async _generateSlickDataForInstanceSyncing(selectedTreeNode = undefined) {
        // TODO : this can be same as Linking function
        await this._loadSlickDropdownsData(selectedTreeNode);
        this._dataForSlickTableSyncing = [];
        this._dataForSlickTableDerivedSyncing = [];
        this.errSyncingIds = [];
        const dataForSyncingCriteria = this._snDataSyncConfigEntity.getDataSyncMappings() || {},
            dataForCurrentClass = dataForSyncingCriteria[selectedTreeNode] || [],
            dataForDerivedClassRules = this._generateDerivedSyncingRules(
                this._classAttributes,
                dataForSyncingCriteria,
                selectedTreeNode
            );

        dataForDerivedClassRules.forEach((item, idx) => {
            this._dataForSlickTableDerivedSyncing.push({
                solutions: this._nodePropData,
                deviceNodeTreeData: this._deviceNodeTreeData,
                ciAttributesDropDownData: this._dataForCiAttrDropdown,
                ciAttributesData: this._classAttributes,
                id: `id_${idx}`,
                ignore: item["ignore"] || false,
                devicePropPath: item["devicePropPath"],
                devicePropType: this.findPathForDeviceProps(
                    item["devicePropPath"],
                    this._nodePropData
                ),
                cmdbAttr: item["cmdbAttr"],
                cmdbClass: this._selectedClassSyncing,
                sourceCmdbClass: item["cmdbClass"],
                cmdbAttrType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                syncDirection: item["syncDirection"],
            });
        });

        dataForCurrentClass.forEach((item, idx) => {
            if (item.ignorePlaceholder !== true) {
                this._dataForSlickTableSyncing.push({
                    solutions: this._nodePropData,
                    deviceNodeTreeData: this._deviceNodeTreeData,
                    ciAttributesDropDownData: this._dataForCiAttrDropdown,
                    ciAttributesData: this._classAttributes,
                    id: `id_${idx}`,
                    ignore: item["ignore"] || false,
                    devicePropPath: item["devicePropPath"],
                    devicePropType: this.findPathForDeviceProps(
                        item["devicePropPath"],
                        this._nodePropData
                    ),
                    cmdbAttr: item["cmdbAttr"],
                    cmdbClass: this._selectedClassSyncing,
                    cmdbAttrType: this.findType(this._dataForCiAttrDropdown, item["cmdbAttr"]),
                    syncDirection: item["syncDirection"],
                });
                //we should track the last used idx so we always use a unique higher value
                this._D2CSIdx = idx;
            }
        });

        this.emit("RefreshDeviceAndCiSyncingGrid");
    }

    addNewD2CSyncingRule() {
        const idx = Math.random() * 100;
        const firstDevicePropNode = this.findFirstNode(this._nodePropData);
        const filteredCiDropDown = this._getFilteredCiAttributeList(
            _.get(firstDevicePropNode, "dataType", "")
        );

        if (!_.isEmpty(this._dataForCiAttrDropdown)) {
            this._dataForSlickTableSyncing.push({
                solutions: this._nodePropData,
                deviceNodeTreeData: this._deviceNodeTreeData,
                ciAttributesDropDownData: this._dataForCiAttrDropdown,
                ciAttributesData: this._classAttributes,
                id: `id_${idx}`,
                ignore: _.get(firstDevicePropNode, "ignore", false),
                devicePropPath: _.get(firstDevicePropNode, "path", []),
                devicePropType: _.get(firstDevicePropNode, "dataType", ""),
                cmdbAttr: filteredCiDropDown ? filteredCiDropDown[0].value : "",
                cmdbAttrType: this.findType(
                    filteredCiDropDown,
                    filteredCiDropDown ? filteredCiDropDown[0].value : ""
                ),
                cmdbClass: this._selectedClassSyncing,
                syncDirection: "DEVICE_TO_CI",
            });

            this.setDataSyncMappings();

            this.emit("RefreshDeviceAndCiSyncingGrid");
        } else {
            const errObj = {
                errorCode: "CI_ATTRIBUTES_ERR",
                errorMsg: `No CI Attributes exist for the selected node. You will not be able to add any Data Sync Mappings for the selected Node`,
                title: "Error",
            };
            return this._muf.displayError(errObj);
        }
    }

    addRemoveD2CSyncingIgnoreRule(item) {
        const dataSyncConfigList = this._snDataSyncConfigEntity.getDataSyncMappings();

        if (!dataSyncConfigList[this._selectedClassSyncing]) {
            dataSyncConfigList[this._selectedClassSyncing] = [];
        }

        const idxActive = dataSyncConfigList[this._selectedClassSyncing].findIndex(
            (el) =>
                JSON.stringify(el.devicePropPath) === JSON.stringify(item.devicePropPath) &&
                el.cmdbAttr === item.cmdbAttr &&
                el.syncDirection === item.syncDirection
        );

        if (idxActive > -1) {
            dataSyncConfigList[this._selectedClassSyncing].splice(idxActive, 1);
            if (
                _.isEmpty(dataSyncConfigList[this._selectedClassSyncing]) &&
                !_.isUndefined(dataSyncConfigList[this._selectedClassSyncing])
            ) {
                delete dataSyncConfigList[this._selectedClassSyncing];
            }
        } else {
            dataSyncConfigList[this._selectedClassSyncing].push({
                ignore: item.ignore,
                devicePropPath: item.devicePropPath,
                cmdbAttr: item.cmdbAttr,
                cmdbClass: this._selectedClassSyncing,
                syncDirection: item.syncDirection,
                ignorePlaceholder: true,
                sourceCmdbClass: item.sourceCmdbClass,
            });
        }

        // Update the entity with the new mappings
        this._snDataSyncConfigEntity.setDataSyncMappings(dataSyncConfigList);

        this.emit("RefreshDeviceAndCiSyncingGrid");
    }

    addRemoveD2CLinkingIgnoreRule(item) {
        const dataLinkConfigList = this._snDataSyncConfigEntity.getInstanceLinkingCriteria();

        if (!dataLinkConfigList[this._selectedClass]) {
            dataLinkConfigList[this._selectedClass] = [];
        }

        const idxActive = dataLinkConfigList[this._selectedClass].findIndex(
            (el) =>
                JSON.stringify(el.devicePropPath) === JSON.stringify(item.devicePropPath) &&
                el.cmdbAttr === item.cmdbAttr
        );

        if (idxActive > -1) {
            dataLinkConfigList[this._selectedClass].splice(idxActive, 1);
            if (
                _.isEmpty(dataLinkConfigList[this._selectedClass]) &&
                !_.isUndefined(dataLinkConfigList[this._selectedClass])
            ) {
                delete dataLinkConfigList[this._selectedClass];
            }
        } else {
            dataLinkConfigList[this._selectedClass].push({
                ignore: item.ignore,
                devicePropPath: item.devicePropPath,
                cmdbAttr: item.cmdbAttr,
                cmdbClass: this._selectedClass,
                ignorePlaceholder: true,
                sourceCmdbClass: item.sourceCmdbClass,
            });
        }

        //we  have to set the mappings
        this._snDataSyncConfigEntity.setInstanceLinkingCriteria(dataLinkConfigList);

        this.emit("RefreshDeviceAndCiLinkingGrid");
    }

    _getFilteredCiAttributeList(devicePropType) {
        const dropDownFilteredData = [];
        this._dataForCiAttrDropdown.forEach((item) => {
            if (this._shouldIncludeDeviceProp(item.type, devicePropType)) {
                dropDownFilteredData.push(item);
            }
        });
        return dropDownFilteredData;
    }
    // Device And Ci Syncing Data - End

    // Class and Device Mapping Data - Start

    _generateSlickDataForClassAndDeviceMapping() {
        this._loadDeviceTypes();

        this._dataForSlickTableMapping = [];
        const dataForNamespaceMapping =
            this._snDataSyncConfigEntity.getNamespaceMatchingCriteria() || [];

        dataForNamespaceMapping.forEach((item, idx) => {
            this._dataForSlickTableMapping.push({
                classTreeData: this._dataForTree,
                classes: this._classes,
                deviceTypeData: this._deviceTypeBase,
                vendorOsMap: this._vendorOsMap,
                id: `id_${idx}`,
                cmdbClass: item["cmdbClass"],
                deviceBaseType: item["deviceBaseType"],
                deviceVendor: item["deviceVendor"],
                deviceOs: item["deviceOs"],
            });
        });

        this.emit("RefreshClassAndDeviceMappingGrid");
    }

    addNewCnDMappingRule() {
        const idx = Math.random() * 100,
            firstType = Object.keys(this._deviceTypeBase)[0];

        this._dataForSlickTableMapping.push({
            classTreeData: this._dataForTree,
            classes: this._classes,
            deviceTypeData: this._deviceTypeBase,
            vendorOsMap: this._vendorOsMap,
            id: `id_${idx}`,
            cmdbClass: "cmdb_ci",
            deviceBaseType: firstType,
            deviceVendor: "*",
            deviceOs: "*",
        });

        this.setNamespaceMatchingCriteria();

        this.emit("RefreshClassAndDeviceMappingGrid");
    }

    // Class and Device Mapping Data - End

    /*******************************************/
    /* END SLICK DATA METHODS                  */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event OSManagementComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    organizationChanged() {
        return Promise.all([]).then(() => {
            this.emit("OrgChange");
        });
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _saveEntity() {
        const pendingPatches = this._snDataSyncConfigEntity.pendingPatches || [];

        if (!this._snDataSyncConfigEntity.isSynchronized()) {
            await this._snDataSyncConfigEntity.create();
        } else if (this._snDataSyncConfigEntity.isDirty()) {
            await this._snDataSyncConfigEntity.update();
        }

        if (await this._runTestConnectionWork(this._serviceConnectionEntity, true)) {
            const shouldLoadMetadata = pendingPatches.some(
                (patch) =>
                    (patch.path === "/enabled" && patch.value === true) ||
                    patch.path === "/serviceConnectionId"
            );

            this._cmdbConnectionFailed = false;
            if (shouldLoadMetadata) {
                await this._loadClasses();
                await this._loadSelectedClasses();
            }

            this.emit("SavedWithGoodConnection");
        } else {
            this._cmdbConnectionFailed = true;
            this.emit("FailedCMDBConnect");
        }

        return Promise.resolve();
    }

    setSchedule(dateTime) {
        this._snDataSyncConfigEntity.setSchedule(dateTime);
        this._snDataSyncConfigEntity.setScheduleWorkInput({
            dataProvider: "serviceNow",
            dataProviderProduct: "cmdb",
        });
    }

    setInstanceLinking() {
        // Retrieve existing linking criteria or initialize a new object
        const dataForLinkingCriteria =
            this._snDataSyncConfigEntity.getInstanceLinkingCriteria() || {};

        // Capture the initial state of dataForLinkingCriteria[this._selectedClass]
        const oldSelectedLinkingCriteria = _.cloneDeep(
            dataForLinkingCriteria[this._selectedClass] || []
        );

        // Update the linking criteria for the selected class by removing specific properties from each item
        dataForLinkingCriteria[this._selectedClass] =
            this._dataForSlickTable.map((item) =>
                _.omit(item, [
                    "ciAttributesData",
                    "ciAttributesDropDownData",
                    "deviceNodeTreeData",
                    "id",
                    "solutions",
                ])
            ) || {};

        // Initialize error handling variables
        this._isDnCLErrored = false;
        this.errLinkingIds = [];

        // Validate each item in the data for linking
        this._dataForSlickTable.forEach((item) => {
            if (item.ignorePlaceholder !== true) {
                if (!this.typeComboAllowed(item.devicePropType, item.cmdbAttrType)) {
                    this._isDnCLErrored = true;
                    this.errLinkingIds.push(item.id);
                }
            }
        });

        // Remove placeholders linked to removed dataForLinkingCriteria
        const newSelectedLinkingCriteria = dataForLinkingCriteria[this._selectedClass];
        const removedCriteriaList = this._findDeletedRules(
            newSelectedLinkingCriteria,
            oldSelectedLinkingCriteria,
            ["devicePropPath", "cmdbAttr"]
        );
        removedCriteriaList.forEach((criteria) => {
            for (const [key, value] of Object.entries(dataForLinkingCriteria)) {
                if (_.isArray(value)) {
                    dataForLinkingCriteria[key] = value.filter((item) => {
                        return !(
                            item.sourceCmdbClass === this._selectedClass &&
                            JSON.stringify(item.devicePropPath) === JSON.stringify(criteria[0]) &&
                            item.cmdbAttr === criteria[1]
                        );
                    });
                }
                if (
                    _.isEmpty(dataForLinkingCriteria[key]) &&
                    !_.isUndefined(dataForLinkingCriteria[key])
                ) {
                    delete dataForLinkingCriteria[key];
                }
            }
        });

        // If no errors, update the instance linking criteria with the new data
        this._snDataSyncConfigEntity.setInstanceLinkingCriteria(dataForLinkingCriteria);
    }

    _findDeletedRules(newRules = [], oldRules = [], criteria = []) {
        const deletedAttributes = [];

        if (_.isArray(criteria) && criteria.length > 0) {
            for (const key in oldRules) {
                const oldRule = oldRules[key];
                const newRule = newRules[key] || [];

                const isDeleted = criteria.some((criterion) => {
                    return (
                        JSON.stringify(newRule[criterion]) !== JSON.stringify(oldRule[criterion])
                    );
                });

                if (isDeleted) {
                    deletedAttributes.push(
                        criteria.map((criterion) => {
                            return oldRule[criterion];
                        })
                    );
                }
            }
        }

        return deletedAttributes;
    }

    setAllowCreationOfNewCI(value) {
        this._snDataSyncConfigEntity.setCreateCis(value);
    }

    setDataSyncMappings() {
        // Retrieve existing syncing mappings or initialize a new object
        const dataSyncMappings = this._snDataSyncConfigEntity.getDataSyncMappings() || {};

        // Capture the initial state of dataForLinkingCriteria[this._selectedClass]
        const oldSelectedDataSyncMappings = _.cloneDeep(
            dataSyncMappings[this._selectedClassSyncing] || []
        );

        // Update the syncing mappings for the selected class by removing specific properties from each item
        dataSyncMappings[this._selectedClassSyncing] =
            this._dataForSlickTableSyncing.map((item) =>
                _.omit(item, [
                    "ciAttributesData",
                    "ciAttributesDropDownData",
                    "deviceNodeTreeData",
                    "id",
                    "solutions",
                ])
            ) || {};

        // Initialize error handling variables
        this._isDnCSErrored = false;
        this.errSyncingIds = [];

        // Validate each item in the data for syncing
        this._dataForSlickTableSyncing.forEach((item) => {
            if (item.ignorePlaceholder !== true) {
                if (item.syncDirection === "DEVICE_TO_CI") {
                    if (!this._shouldIncludeDeviceProp(item.cmdbAttrType, item.devicePropType)) {
                        this._isDnCSErrored = true;
                        this.errSyncingIds.push(item.id);
                    }
                } else if (item.syncDirection === "CI_TO_DEVICE") {
                    if (!this._shouldIncludeNodeCiToDA(item.devicePropType, item.cmdbAttrType)) {
                        this._isDnCSErrored = true;
                        this.errSyncingIds.push(item.id);
                    }
                }
            }
        });

        // Remove placeholders linked to removed dataSyncMappings
        const newSelectedDataSyncMappings = dataSyncMappings[this._selectedClassSyncing];
        const removedCriteriaList = this._findDeletedRules(
            newSelectedDataSyncMappings,
            oldSelectedDataSyncMappings,
            ["devicePropPath", "cmdbAttr", "syncDirection"]
        );
        removedCriteriaList.forEach((criteria) => {
            for (const [key, value] of Object.entries(dataSyncMappings)) {
                if (_.isArray(value)) {
                    dataSyncMappings[key] = value.filter((item) => {
                        return !(
                            item.sourceCmdbClass === this._selectedClass &&
                            JSON.stringify(item.devicePropPath) === JSON.stringify(criteria[0]) &&
                            item.cmdbAttr === criteria[1] &&
                            item.syncDirection === criteria[2]
                        );
                    });
                }
                if (_.isEmpty(dataSyncMappings[key]) && !_.isUndefined(dataSyncMappings[key])) {
                    delete dataSyncMappings[key];
                }
            }
        });

        // If no errors, update the instance syncing mappings with the new data
        this._snDataSyncConfigEntity.setDataSyncMappings(dataSyncMappings);
    }

    validateLinking() {
        let errExists = false;
        this._isDnCLErrored = false;
        let err = "Errors were found while linking Device Property and CMDB Class Attribute: \n\n";
        this.errLinkingIds = [];

        (this._dataForSlickTable || []).forEach((item) => {
            if (item.ignorePlaceholder !== true) {
                if (!this.typeComboAllowed(item.devicePropType, item.cmdbAttrType)) {
                    err += `${item.devicePropType} and ${item.cmdbAttrType} \n\n\n`;
                    errExists = true;
                    this._isDnCLErrored = true;
                    this.errLinkingIds.push(item.id);
                }
            }
        });

        if (errExists) {
            return {
                valid: false,
                errorMsg: err,
            };
        }
        return {valid: true};
    }

    validateSyncing() {
        let errExists = false;
        this._isDnCSErrored = false;
        let err = "Errors were found while Syncing Device Property and CMDB Class Attribute: \n\n";
        this.errSyncingIds = [];

        (this._dataForSlickTableSyncing || []).forEach((item) => {
            if (item.ignorePlaceholder !== true) {
                if (item.syncDirection === "DEVICE_TO_CI") {
                    if (!this._shouldIncludeDeviceProp(item.cmdbAttrType, item.devicePropType)) {
                        err += `${item.devicePropType} and ${item.cmdbAttrType} \n\n\n`;
                        errExists = true;
                        this._isDnCSErrored = true;
                        this.errSyncingIds.push(item.id);
                    }
                } else if (item.syncDirection === "CI_TO_DEVICE") {
                    if (!this._shouldIncludeNodeCiToDA(item.devicePropType, item.cmdbAttrType)) {
                        err += `${item.devicePropType} and ${item.cmdbAttrType} \n\n\n`;
                        errExists = true;
                        this._isDnCSErrored = true;
                        this.errSyncingIds.push(item.id);
                    }
                }
            }
        });

        if (errExists) {
            this.emit("ErrorInSyncing", this.errSyncingIds);
            return {
                valid: false,
                errorMsg: err,
            };
        }
        return {valid: true};
    }

    setNamespaceMatchingCriteria() {
        const namespaceMatchingCriteria =
            this._dataForSlickTableMapping.map((item) =>
                _.omit(item, [
                    "classTreeData",
                    "classes",
                    "deviceNodeTreeData",
                    "id",
                    "deviceTypeData",
                    "vendorOsMap",
                ])
            ) || {};

        this._snDataSyncConfigEntity.setNamespaceMatchingCriteria(namespaceMatchingCriteria);
    }

    async resetAllPatches() {
        await this._snDataSyncConfigEntity.clearPendingPatches();
        this._isDnCLErrored = false;
        this._isDnCSErrored = false;
        this.emit("ResetAllValues");
    }

    /*******************************************/
    /* END ENTITY METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    deleteInstanceLinkingCriteria(id) {
        this._dataForSlickTable = this._dataForSlickTable.filter((item) => item.id !== id);
    }

    deleteInstanceSyncingCriteria(id) {
        this._dataForSlickTableSyncing = this._dataForSlickTableSyncing.filter(
            (item) => item.id !== id
        );
    }

    deleteNamespaceMatchingCriteria(id) {
        this._dataForSlickTableMapping = this._dataForSlickTableMapping.filter(
            (item) => item.id !== id
        );
    }

    loadOrganizationData() {
        return this._organizationService.getById({id: this.activeOrgId}).then((orgData) => {
            this._organizationData = orgData;
            this._triggerOrganizationDataChanged(orgData);
            return orgData;
        });
    }

    async _loadSlickDropdownsData(selectedTreeNode) {
        try {
            await this._loadDevicePropsData();
            await this._loadMappingAttributes(selectedTreeNode);
        } catch (err) {
            return Promise.reject(err);
        }
    }

    async _loadDevicePropsData() {
        try {
            if (_.isEmpty(this._nodePropData)) {
                if (!this._serviceConnectionEntity) {
                    this._nodePropData = {};
                    this._deviceNodeTreeData = this._generateTreeDataForNodeProps(
                        this._nodePropData
                    );
                    return Promise.resolve();
                }
                const workInput = {
                    dataProvider: "serviceNow",
                    dataProviderProduct: "cmdb",
                    metaDataOptions: {loadDeviceExplorables: true},
                };
                const workResults = await this._snDataSyncConfigEntity.runMetaDataWork(workInput);

                const errorMsg = _.get(workResults, "summary.errorMsg", "");

                if (errorMsg) {
                    throw new Error(`Metadata work failed: ${errorMsg}`);
                }
                this._nodePropData = _.get(workResults, "summary.metadata.deviceExplorables", {});
                this._deviceNodeTreeData = this._generateTreeDataForNodeProps(this._nodePropData);
            }
            return Promise.resolve();
        } catch (err) {
            this._logger.warn(err, "ServiceNowComponent._loadDevicePropsData() error.");
        }
    }

    async _loadMappingAttributes(selectedNode = ["cmdb_ci"]) {
        try {
            // Validate input
            if (!selectedNode || (!_.isArray(selectedNode) && !_.isString(selectedNode))) {
                throw new Error(`Invalid selectedNode: ${selectedNode}`);
            }

            // Ensure required dependencies exist
            if (!this._snDataSyncConfigEntity) {
                throw new Error("_snDataSyncConfigEntity is not available");
            }

            if (this._serviceConnectionEntity) {
                let classQuery = {};
                if (_.isArray(selectedNode)) {
                    selectedNode.forEach((node) => Object.assign(classQuery, {[node]: true}));
                } else if (_.isString(selectedNode)) {
                    classQuery = {[selectedNode]: true};
                }

                //loads only child and all needed parent attributes
                const workInput = {
                    dataProvider: "serviceNow",
                    dataProviderProduct: "cmdb",
                    loadClassAttributes: true,
                    metaDataOptions: {
                        loadMappableAttributes: true,
                        classNames: classQuery,
                        includeDerived: true,
                    },
                };
                const workResults = await this._snDataSyncConfigEntity.runMetaDataWork(workInput);

                const errorMsg = _.get(workResults, "summary.errorMsg", "");

                if (errorMsg) {
                    throw new Error(`Metadata work failed: ${errorMsg}`);
                }
                this._classAttributes = _.get(
                    workResults,
                    "summary.metadata.mappableAttributes",
                    {}
                );
            } else {
                this._classAttributes = {};
            }

            // this._classAttributes = await this._snDataSyncConfigEntity.loadMappableAttributes({
            //     classNames: classQuery,
            //     includeDerived: true,
            // });

            const attributes = {};
            for (const node of Object.values(this._classAttributes)) {
                //no spread operator for now
                Object.assign(attributes, node.attributes || {});
            }
            this._dataForCiAttrDropdown = _.keys(attributes).map((item) => {
                return {
                    label: attributes[item].displayName,
                    value: item,
                    type: attributes[item].type,
                };
            });

            this._dataForCiAttrDropdown.sort((a, b) => a.label.localeCompare(b.label));

            // this._dataForTreeAttributes = this.generateTreeForAttribute(this._classAttributes);
        } catch (err) {
            this._logger.warn(err, "ServiceNowComponent._loadMappingAttributes() error.");
        }
    }

    async _loadDataSyncEntity() {
        //     this._snDataSyncConfigEntity = await this._dataSyncConfig.loadProviderConfig(
        //         this.activeOrgId,
        //         "serviceNow",
        //         "cmdb"
        //     );
        //     if (_.isEmpty(this._snDataSyncConfigEntity)) {
        //         this._snDataSyncConfigEntity = new this._dataSyncConfig(this.activeOrgId);
        //         this._snDataSyncConfigEntity.setDataProvider("serviceNow");
        //         this._snDataSyncConfigEntity.setDataProviderProduct("cmdb");
        //     }
        //     return Promise.resolve();
    }

    async _loadClasses() {
        try {
            if (!this._serviceConnectionEntity) {
                this._classes = {};
                this._dataForTree = this.generateTreeForClasses(this._classes);
                return Promise.resolve();
            }
            const workInput = {
                dataProvider: "serviceNow",
                dataProviderProduct: "cmdb",
                metaDataOptions: {
                    classNames: {
                        cmdb_ci: true,
                    },
                    includeAscendants: false,
                    includeDescendants: true,
                    loadNamespaces: true,
                },
            };
            const workResults = await this._snDataSyncConfigEntity.runMetaDataWork(workInput);

            const errorMsg = _.get(workResults, "summary.errorMsg", "");

            if (errorMsg) {
                throw new Error(`Metadata work failed: ${errorMsg}`);
            }
            // this._classes = await this._snDataSyncConfigEntity.loadNamespaces({
            //     classNames: {
            //         cmdb_ci: true,
            //     },
            //     includeAscendants: false,
            //     includeDescendants: true,
            // });
            this._classes = _.get(workResults, "summary.metadata.namespaces", {});
            this._dataForTree = this.generateTreeForClasses(this._classes);

            return Promise.resolve();
        } catch (err) {
            // this._cmdbConnectionFailed = true;
            return this._muf.hideDisplayed().then(() => {
                const err = {
                    errorCode: "METADATA_WORK_FAILED",
                    errorMsg: "Unable to connect to ServiceNow.",
                    errorTitle: "Metadata Work Failed",
                };
                this._muf.displayError(err);
                return Promise.resolve();
            });
        }
    }

    async _loadSelectedClasses() {
        try {
            if (!this._serviceConnectionEntity) {
                this._classesFiltered = {};
                this._dataForTreeFiltered = this.generateTreeForClasses(this._classesFiltered);
                return Promise.resolve();
            }
            const availClasses = new Set(
                    (this._snDataSyncConfigEntity.getNamespaceMatchingCriteria() || []).map(
                        (item) => item.cmdbClass
                    )
                ),
                classesObject = {};
            availClasses.forEach((i) => (classesObject[i] = true));

            const workInput = {
                dataProvider: "serviceNow",
                dataProviderProduct: "cmdb",
                metaDataOptions: {
                    classNames: classesObject,
                    includeAscendants: true,
                    includeDescendants: false,
                    loadNamespaces: true,
                },
            };
            const workResults = await this._snDataSyncConfigEntity.runMetaDataWork(workInput);

            // this._classesFiltered = await this._snDataSyncConfigEntity.loadNamespaces({
            //     classNames: classesObject,
            //     includeAscendants: true,
            //     includeDescendants: false,
            // });
            this._classesFiltered = _.get(workResults, "summary.metadata.namespaces", {});
            this._dataForTreeFiltered = this.generateTreeForClasses(this._classesFiltered);

            return Promise.resolve();
        } catch (err) {
            this._cmdbConnectionFailed = true;
            return Promise.resolve();
        }
    }

    _loadDeviceTypes() {
        this._deviceTypeBase = this._DeviceEntityDef.getDetectedRawTypeBaseLabels();
    }

    _loadVendorOsMap() {
        this._ValueMapEntity.loadVendorOsMapEntity(this.activeOrgId).then((entity) => {
            if (entity) {
                this._vendorOsMap = entity.getValueMap();
                return entity;
            }
            this.emit("NoVendorMap");
            this._noVendorMap = true;
        });
    }

    _getConnectionOptions() {
        return this._serviceConnectionEntities.map((entity) => {
            return {
                label: entity.name,
                value: entity.id,
            };
        });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /* START RUNWORK METHODS MEtHODS           */
    /*******************************************/

    async downloadLastSyncReport() {
        let latestWork = await this._snDataSyncConfigEntity.loadRelatedWorks(
            {},
            {},
            {_id: -1},
            {skip: 0, limit: 1}
        );
        if (latestWork.length > 0) {
            latestWork = latestWork[0];
        }

        const workReport = (await latestWork?.loadRelatedReports())?.[0] ?? {};
        const workLog = (await latestWork?.loadRelatedLog())?.log ?? {};

        const hasReport = !_.isEmpty(workReport);
        const hasLog = !_.isEmpty(workLog);

        if (!hasReport && !hasLog) {
            const err = {
                errorCode: "WORK_ERROR",
                errorMsg:
                    "No report or log available. Please initiate a data sync to generate them.",
                errorTitle: "Error In Downloading Last Sync Data",
            };
            await this._muf.displayError(err);
            return;
        }

        const fileConfig = {};
        const date = workReport?.updatedOn || workReport?.createdOn || new Date();

        if (hasReport) {
            const csvReport = workReport.report;
            const reportFileName = this._renderer._fileNamer({
                solution: "SS",
                general: "CMDBIntegration-LastReport",
                specific: this._organizationData.name,
                date,
            });

            fileConfig[reportFileName + ".csv"] = {
                type: "text/csv",
                content: csvReport,
            };
        }

        if (hasLog) {
            const logFileName = this._renderer._fileNamer({
                solution: "SS",
                general: "CMDBIntegration-LastLog",
                specific: this._organizationData.name,
                date,
            });

            fileConfig[logFileName + ".json"] = {
                type: "application/json",
                content: JSON.stringify(workLog, null, "\t"),
            };

            const logLines = workLog.map((entry) => `${entry.time} ${entry.msg}`).join("\n");
            fileConfig[logFileName + ".txt"] = {
                type: "text/plain",
                content: logLines,
            };
        }

        const zipFileName = this._renderer._fileNamer({
            solution: "SS",
            general: "CMDBIntegration-LastSync",
            specific: this._organizationData.name,
            date,
            suffix: "zip",
        });

        this._fileFactory.saveStringsAsZip(fileConfig, zipFileName);

        if (!hasReport) {
            const err = {
                errorCode: "PARTIAL_DOWNLOAD",
                errorMsg:
                    "No report available. Only log downloaded. Please run a new data sync to generate a complete report.",
                errorTitle: "Partial Download",
            };
            await this._muf.displayError(err);
        } else if (!hasLog) {
            const err = {
                errorCode: "PARTIAL_DOWNLOAD",
                errorMsg:
                    "No log available. Only report downloaded. Please run a new data sync to generate a complete log.",
                errorTitle: "Partial Download",
            };
            await this._muf.displayError(err);
        }
    }

    syncWork(preview = false) {
        const workInput = {
            dataProviderProduct: "cmdb",
            dataProvider: "serviceNow",
            syncOptions: {
                preview,
                isEditable: true,
            },
        };

        const schedName = `ServiceNow CMDB ${preview ? "Preview " : ""}Sync from Settings`;
        const schedDescription = preview
            ? "Preview data synchronization between ServiceNow CMDB and Gluware"
            : "Synchronize data between ServiceNow CMDB and Gluware";
        const workTarget = {
            entityType: "Device",
            category: "entity",
            method: "query",
            query: {},
        };

        // Schedule 2 minutes ahead as buffer
        const now = new Date();
        now.setMinutes(now.getMinutes() + 2);
        const schedule = {
            type: "one",
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            on: {
                year: now.getFullYear(),
                month: now.getMonth(),
                date: now.getDate(),
                hour: now.getHours(),
                minute: now.getMinutes(),
            },
        };

        return this._DeviceEntityDef
            .scheduleDataSyncWork(
                this.activeOrgId,
                schedName,
                schedDescription,
                schedule,
                workTarget,
                workInput
            )
            .then((resultEntity) => {
                return this._muf
                    .displayConfirmation(
                        "The sync has been scheduled and will start in about 2 minutes. You can monitor progress and download logs in the Schedules solution.",
                        {
                            title: `${preview ? "Preview" : "Run"} Syncing Now`,
                            confirmDetails: {
                                text: "View in Schedules",
                                type: "additive",
                            },
                            rejectDetails: {
                                text: "Close",
                                type: "text",
                            },
                        }
                    )
                    .then(() => {
                        return this._router.goTo({
                            app: "GameConsole",
                            s: "GameCenter",
                            sln: "WorkTriggers",
                            exp: "WorkTriggers",
                            rd: {
                                openEditor: true,
                                selection: [resultEntity.getId()],
                            },
                        });
                    });
            })
            .catch((err) => {
                if (err.userRejected) {
                    return this._muf.hideDisplayed();
                }
                this._logger.warn(err, "ServiceNowComponent.SyncWork() error.");
                return this._muf.displayError({
                    errorCode: "ERROR",
                    errorMsg: err.message,
                });
            });
    }

    async _runTestConnectionWork(connEntity, silentCheck = false) {
        try {
            if (!connEntity) {
                if (this._snDataSyncConfigEntity) {
                    const {serviceConnectionId, pristineProperties} = this._snDataSyncConfigEntity;
                    if (serviceConnectionId === _.get(pristineProperties, "serviceConnectionId")) {
                        this.emit("ConnectionFailed");
                    }
                } else {
                    this.emit("ConnectionFailed");
                }
                return false;
            }

            // Active org, not the connection's owning org: an inherited connection's tenant is an
            // ancestor the caller may not be authorized to launch work against.
            const workTarget = {
                category: "entity",
                entityType: "ServiceConnection",
                method: "single",
                id: connEntity.id,
            };
            const res = await this._ServiceConnectionEntityDef.runTestConnectionWork(
                this.activeOrgId,
                workTarget
            );

            if (_.get(res, "summary.errorMsg", false)) {
                this._logger.warn(
                    _.get(res, "summary.errorMsg", false),
                    "ServiceNowComponent._runTestConnectionWork() error."
                );
                if (!silentCheck) {
                    const userMsg = [
                        "Failed to connect to ServiceNow CMDB.",
                        "New changes will take effect after Save.\r\n",
                    ].join("\r\n\r\n");
                    const errObj = {
                        errorCode: "CONNECTION_ERROR",
                        errorMsg: userMsg,
                        title: "Connection Failed",
                    };
                    this._muf.displayError(errObj);
                }
                /**
                 * This condition checks if we test and connection has not changed but still the connection succeeded or failed.
                 * It will change the connection status
                 */
                if (
                    this._snDataSyncConfigEntity.serviceConnectionId ===
                    this._snDataSyncConfigEntity.pristineProperties.serviceConnectionId
                ) {
                    this.emit("ConnectionFailed");
                }
                return false;
            } else {
                if (!silentCheck) {
                    const userMsg = [
                        "Successfully connected to ServiceNow CMDB.",
                        "You will need to Save before all tabs will refresh.\r\n",
                    ].join("\r\n\r\n");
                    await this._muf.displayNotification(userMsg, {
                        notDismissable: true,
                        title: "Connection Successful",
                        confirmDetails: {text: "OK"},
                    });
                }

                /**
                 * This condition checks if we test and connection has not changed but still the connection succeeded or failed.
                 * It will change the connection status
                 */
                if (
                    this._snDataSyncConfigEntity.serviceConnectionId ===
                    this._snDataSyncConfigEntity.pristineProperties.serviceConnectionId
                ) {
                    this.emit("ConnectionSuccessful");
                    await this._loadClasses();
                    await this._loadSelectedClasses();
                    this._cmdbConnectionFailed = false;
                    this.emit("SavedWithGoodConnection");
                }
                return true;
            }
        } catch (err) {
            this._logger.warn(err, "ServiceNowComponent._runTestConnectionWork() error.");
        }
    }

    /*******************************************/
    /* END RUNWORK METHODS MEtHODS             */
    /* START HELPER MEtHODS                    */
    /*******************************************/

    findCiAttrType(entries, valueToFind) {
        for (const entry of entries) {
            if (entry.value === valueToFind) {
                return entry.type;
            }
        }
        return null;
    }

    typeComboAllowed(devicePropType, cmdbAttrType) {
        const allowedTypes = {
            string: ["string", "GUID", "ip_addr", "glide_date_time", "glide_date"],
            number: ["integer", "decimal", "float"],
            boolean: ["boolean"],
            unixTime: ["GUID", "glide_date_time", "glide_date"],
            duration: ["integer"],
            enum: ["string"],
        };

        return allowedTypes[devicePropType] && allowedTypes[devicePropType].includes(cmdbAttrType);
    }

    findFirstNode(obj) {
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                if (_.isObject(obj[key]) && obj[key] !== null) {
                    if ("dataType" in obj[key] && "heading" in obj[key] && "path" in obj[key]) {
                        return obj[key];
                    } else {
                        const result = this.findFirstNode(obj[key]);
                        if (result) {
                            return result;
                        }
                    }
                }
            }
        }
    }

    // Find type for the CmdbCiAttribute
    findType(entries, valueToFind) {
        for (const entry of entries) {
            if (entry.value === valueToFind) {
                return entry.type;
            }
        }
        return null;
    }

    // Find path for the deviceProps
    findPathForDeviceProps(input, obj) {
        for (const key in obj) {
            if (
                key === "path" &&
                Array.isArray(obj[key]) &&
                obj[key].join(".") === input.join(".")
            ) {
                return obj.dataType; // Return the dataType if path matches
            } else if (typeof obj[key] === "object") {
                const result = this.findPathForDeviceProps(input, obj[key]);
                if (result) {
                    return result;
                }
            }
        }
        return null; // If not found, return null
    }

    // Device Node Prop Tree Data generation - Start
    _generateTreeDataForNodeProps(explorables) {
        const restructured = [];
        for (let key in explorables) {
            restructured.push(this._generateTreeNodeForNodeProps(key, explorables[key]));
        }
        return restructured;
    }

    _generateTreeNodeForNodeProps(name, nodeData) {
        if (!nodeData.path) {
            const node = {};
            node.text = name;
            node.children = [];
            for (let key in nodeData) {
                node.children.push(this._generateTreeNodeForNodeProps(key, nodeData[key]));
            }
            return node;
        }
        return name;
    }

    _generateTreeDataForNodePropsFiltered(explorables, dataType) {
        const restructured = [];
        for (let key in explorables) {
            const nodeData = explorables[key];
            if (this._shouldIncludeNode(nodeData, dataType)) {
                restructured.push(this._generateTreeNodeForNodeProps(key, nodeData));
            }
        }
        return restructured;
    }

    _shouldIncludeNode(nodeData, dataType) {
        const typeRules = {
            string: ["string", "GUID", "ip_addr"],
            number: ["integer", "decimal", "float"],
            boolean: ["boolean"],
            unixTime: ["glide_date_time", "glide_date"],
        };

        if (dataType in typeRules) {
            return typeRules[dataType].includes(nodeData.dataType);
        }

        return false;
    }

    // Device Node Prop Tree Data generation - End

    generateTreeForClasses(input) {
        const nodesMap = {};

        let idx = 1;
        for (const key in input) {
            if (input.hasOwnProperty(key)) {
                const node = input[key];
                const parentNodeKey = node.parent || null;
                const newNode = {
                    id: idx,
                    text: node.displayName,
                    state: {opened: true},
                    value: key,
                    children: [],
                };

                if (nodesMap[key]) {
                    newNode.children = nodesMap[key].children;
                }
                nodesMap[key] = newNode;

                if (parentNodeKey) {
                    if (!nodesMap[parentNodeKey]) {
                        nodesMap[parentNodeKey] = {children: []};
                    }
                    nodesMap[parentNodeKey].children.push(newNode);
                }
                idx++;
            }
        }

        const topLevelKeys = Object.keys(input).filter((key) => !input[key].parent);

        const tree = topLevelKeys.map((key) => nodesMap[key]);

        return tree;
    }

    /**
     * _shouldIncludeNodeCiToDA
     * @param {*} nodeDataType
     * @param {*} ciDataType
     * @returns
     */
    _shouldIncludeNodeCiToDA(nodeDataType, ciDataType) {
        const typeRules = {
            string: ["string"],
            GUID: ["string"],
            ip_addr: ["string"],
            integer: ["number", "duration", "string"],
            decimal: ["number", "string"],
            float: ["number", "string"],
            boolean: ["boolean"],
            glide_date_time: ["unixTime", "string"],
            glide_date: ["unixTime", "string"],
        };

        if (ciDataType in typeRules) {
            return typeRules[ciDataType].includes(nodeDataType);
        }

        return false;
    }

    /**
     * _shouldIncludeDeviceProp
     * @param {*} ciDataType
     * @param {*} nodeDataType
     * @returns
     */
    _shouldIncludeDeviceProp(ciDataType, nodeDataType) {
        const typeRules = {
            string: ["string", "GUID", "ip_addr"],
            number: ["integer", "decimal", "float", "string"],
            duration: ["integer"],
            boolean: ["boolean"],
            unixTime: ["glide_date_time", "glide_date"],
            enum: ["string"],
        };

        if (nodeDataType in typeRules) {
            return typeRules[nodeDataType].includes(ciDataType);
        }

        return false;
    }

    /*******************************************/
    /* END HELPER MEtHODS                      */
    /*******************************************/
}

module.exports = ServiceNowComponent;
